// composables/useCookieManager.ts

import { CookieOptions } from '#app';

/**
 * Predefined cookie durations (in seconds)
 */
export const COOKIE_DURATIONS = {
    oneDay: 60 * 60 * 24,
    twoWeeks: 60 * 60 * 24 * 14,
    oneMonth: 60 * 60 * 24 * 30,
    threeMonths: 60 * 60 * 24 * 90,
    sixMonths: 60 * 60 * 24 * 180,
    oneYear: 60 * 60 * 24 * 365,
} as const;

export type CookieDuration = keyof typeof COOKIE_DURATIONS;

export const useCookieManager = () => {
    /**
     * Check if a cookie exists and is set to 'true'
     */
    const isCookieSet = (cookieName: string): boolean => !!useCookie(cookieName).value;

    /**
     * Set a cookie with a preset duration or custom maxAge
     * @param cookieName - Name of the cookie
     * @param duration - Preset duration key or custom maxAge in seconds
     * @param value - Cookie value (default: 'true')
     */
    const setCookie = (cookieName: string, duration: CookieDuration | number = 'twoWeeks', value: string = 'true', options: Omit<CookieOptions<string>, 'readonly'> = {}): void => {
        const maxAge = typeof duration === 'string' ? COOKIE_DURATIONS[duration] : duration;

        const cookie = useCookie(cookieName, {
            path: '/',
            sameSite: 'lax',
            secure: true,
            ...options,
            maxAge,
        });

        cookie.value = value;
    };

    /**
     * Remove a cookie
     */
    const removeCookie = (cookieName: string): void => {
        const cookie = useCookie(cookieName, {
            maxAge: -1,
            path: '/',
        });

        cookie.value = null;
    };

    return {
        isCookieSet,
        setCookie,
        removeCookie,
        COOKIE_DURATIONS,
    };
};
